import React, { useMemo, useRef, useState } from "react";
import {
  FaDownload,
  FaLink,
  FaQrcode,
  FaRupeeSign,
  FaShareAlt,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { motion } from "framer-motion";

const defaultForm = {
  name: "",
  upiId: "",
  amount: "",
  note: "",
  expiryDate: "",
  singleUse: false,
  password: "",
};

const UserUpi = () => {
  const [formData, setFormData] = useState(defaultForm);
  const [qrStyle, setQrStyle] = useState({
    fgColor: "#0f172a",
    bgColor: "#ffffff",
    logo: "",
  });
  const [showError, setShowError] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const qrCodeRef = useRef(null);

  const isUpiValid = formData.upiId.includes("@");

  const generatedLink = useMemo(() => {
    if (!isUpiValid) {
      return "";
    }

    const params = new URLSearchParams({
      pa: formData.upiId,
      pn: formData.name || "Customer",
      cu: "INR",
    });

    if (formData.amount) {
      params.append("am", formData.amount);
    }

    if (formData.note) {
      params.append("tn", formData.note);
    }

    return `upi://pay?${params.toString()}`;
  }, [formData, isUpiValid]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleQrStyle = (event) => {
    const { name, value } = event.target;
    setQrStyle((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      setQrStyle((prev) => ({ ...prev, logo: loadEvent.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const onGenerate = () => {
    setShowError(!isUpiValid);
  };

  const copyLink = async () => {
    if (!generatedLink) {
      return;
    }
    await navigator.clipboard.writeText(generatedLink);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1500);
  };

  const openInUpi = () => {
    if (generatedLink) {
      window.location.href = generatedLink;
    }
  };

  const downloadQr = () => {
    if (!qrCodeRef.current) {
      return;
    }

    const svg = qrCodeRef.current.querySelector("svg");
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      const safeName = formData.upiId ? formData.upiId.split("@")[0] : "payment";
      downloadLink.download = `${safeName}-upi-qr.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-slate-900">
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-[#FAF9F6]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-900 text-white font-black">U</div>
            <span className="font-bold sm:text-lg">Create Custom PayLink</span>
          </Link>
          <Link
            to="/"
            className="rounded-full border border-slate-300 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-100 transition"
          >
            ← Back to Sponsorship Hub
          </Link>
        </div>
      </header>

      <main className="px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-8 shadow-sm"
          >
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Create your sponsorship link</h1>
            <p className="mt-2 text-sm text-slate-500 font-medium">
              Configure your receiver details, generate an instant link, and customize the QR code for your software repository.
            </p>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Maintainer Name (optional)</label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Abhishek"
                  className="w-full rounded-xl border border-slate-200 bg-[#FAF9F6] px-4 py-3 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Receiver UPI ID *</label>
                <input
                  name="upiId"
                  value={formData.upiId}
                  onChange={handleChange}
                  placeholder="name@upi"
                  className="w-full rounded-xl border border-slate-200 bg-[#FAF9F6] px-4 py-3 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                />
                {showError && !isUpiValid && (
                  <p className="mt-2 text-xs font-semibold text-rose-600">Please enter a valid UPI ID containing the @ symbol.</p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Preset Amount (optional)</label>
                <div className="relative">
                  <FaRupeeSign className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="500"
                    className="w-full rounded-xl border border-slate-200 bg-[#FAF9F6] py-3 pl-9 pr-4 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Expiry date</label>
                <input
                  type="date"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-200 bg-[#FAF9F6] px-4 py-3 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Payment note (optional)</label>
                <input
                  name="note"
                  value={formData.note}
                  onChange={handleChange}
                  placeholder="Open Source Project Sponsorship"
                  className="w-full rounded-xl border border-slate-200 bg-[#FAF9F6] px-4 py-3 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                />
              </div>

              <div className="sm:col-span-2 grid gap-3 rounded-2xl border border-slate-200 bg-[#FAF9F6] p-4 sm:grid-cols-2 sm:items-center">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    name="singleUse"
                    checked={formData.singleUse}
                    onChange={handleChange}
                    className="h-4 w-4 rounded text-slate-900"
                  />
                  Single-use payment link
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Optional access password"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">QR Pattern Color</label>
                <input
                  type="color"
                  name="fgColor"
                  value={qrStyle.fgColor}
                  onChange={handleQrStyle}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-[#FAF9F6] p-1 cursor-pointer"
                />
              </div>

              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">QR Background</label>
                <input
                  type="color"
                  name="bgColor"
                  value={qrStyle.bgColor}
                  onChange={handleQrStyle}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-[#FAF9F6] p-1 cursor-pointer"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Logo overlay (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="block w-full text-xs text-slate-500 file:mr-3 file:rounded-xl file:border-0 file:bg-slate-900 file:px-4 file:py-2.5 file:text-xs file:font-bold file:text-white hover:file:bg-slate-800"
                />
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={onGenerate}
                className="w-full rounded-2xl bg-slate-900 px-6 py-3.5 text-sm font-bold text-white hover:bg-slate-800 shadow-sm sm:w-auto"
              >
                Generate Sponsorship Link
              </button>
              <button
                onClick={() => {
                  setFormData(defaultForm);
                  setQrStyle({ fgColor: "#0f172a", bgColor: "#ffffff", logo: "" });
                  setShowError(false);
                }}
                className="w-full rounded-2xl border border-slate-300 px-6 py-3.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto"
              >
                Reset Form
              </button>
            </div>
          </motion.section>

          <motion.aside
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-8 shadow-sm lg:sticky lg:top-24"
          >
            <h2 className="text-xl font-bold text-slate-900">Live Preview & Share</h2>
            <p className="mt-1 text-xs text-slate-500">Your generated link and dynamic QR will render here.</p>

            {generatedLink ? (
              <div className="mt-5 space-y-5">
                <div className="rounded-2xl border border-slate-200 bg-[#FAF9F6] p-4">
                  <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-500">
                    <span className="inline-flex items-center gap-1.5"><FaLink /> Shareable Link</span>
                    <button onClick={copyLink} className="font-bold text-indigo-600 hover:text-indigo-800">
                      {isCopied ? "✓ Copied!" : "Copy Link"}
                    </button>
                  </div>
                  <p className="break-all text-xs text-slate-800 font-mono">{generatedLink}</p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-[#FAF9F6] p-5 text-center flex flex-col items-center" ref={qrCodeRef}>
                  <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-200 inline-block">
                    <QRCodeSVG
                      value={generatedLink}
                      size={200}
                      level="H"
                      includeMargin
                      fgColor={qrStyle.fgColor}
                      bgColor={qrStyle.bgColor}
                      imageSettings={
                        qrStyle.logo
                          ? { src: qrStyle.logo, height: 38, width: 38, excavate: true }
                          : undefined
                      }
                    />
                  </div>
                  <p className="mt-3 text-xs font-medium text-slate-500">Scan with any mobile UPI app</p>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <button
                    onClick={downloadQr}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-3 text-xs font-bold text-slate-800 hover:bg-slate-50 shadow-sm"
                  >
                    <FaDownload /> Download QR
                  </button>
                  <button
                    onClick={openInUpi}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-xs font-bold text-white hover:bg-indigo-700 shadow-sm"
                  >
                    <FaQrcode /> Launch in App
                  </button>
                </div>

                <div className="rounded-xl border border-slate-200 bg-[#FAF9F6] p-4 text-xs text-slate-600">
                  <p className="font-bold text-slate-800">Ready to Share</p>
                  <p className="mt-1 inline-flex items-center gap-2 text-slate-500"><FaShareAlt /> Share directly in GitHub README, Telegram, Discord, or WhatsApp.</p>
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-2xl border-2 border-dashed border-slate-200 bg-[#FAF9F6] p-8 text-center">
                <FaQrcode className="mx-auto text-4xl text-slate-400 mb-2" />
                <p className="text-xs font-semibold text-slate-600">Enter a valid UPI ID and click Generate Sponsorship Link.</p>
              </div>
            )}
          </motion.aside>
        </div>
      </main>
    </div>
  );
};

export default UserUpi;
